import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "../../lib/api";
import { Badge } from "../../components/Pill";

const fmt = (n) => (n >= 1e6 ? (n / 1e6).toFixed(1) + "M" : n >= 1e3 ? (n / 1e3).toFixed(1) + "K" : String(n ?? 0));

export default function AnalyticsTab() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api("/admin/analytics").then(setData).catch(() => setData(false));
  }, []);

  if (!data) return <p className="font-elegant italic text-xl text-gold animate-pulse">Crunching the numbers</p>;
  if (data === false) return <p className="text-sm text-neutral-500">Analytics unavailable.</p>;

  const cards = [
    { label: "Views Today", value: fmt(data.today) },
    { label: "Last 7 Days", value: fmt(data.week) },
    { label: "Last 30 Days", value: fmt(data.month) },
    { label: "Lifetime Views", value: fmt(data.lifetime) },
  ];

  return (
    <div data-testid="analytics-tab">
      <h2 className="font-display uppercase text-3xl text-ink">Analytics</h2>
      <p className="mt-2 text-sm text-neutral-500">
        Daily traffic across the site, with lifetime totals including the historical archive baseline.
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
        {cards.map((c) => (
          <div key={c.label} data-testid={`analytics-card-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="bg-white border-2 border-gold/10 rounded-[1.5rem] p-7">
            <p className="font-display text-4xl text-champagne">{c.value}</p>
            <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.2em] text-ink/60">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-white border-2 border-gold/10 rounded-[1.5rem] p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display uppercase text-xl text-ink">Daily Views</h3>
          <Badge tone="blush">Last 30 days</Badge>
        </div>
        <div className="h-72" data-testid="analytics-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="viewsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#F4C2C2" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(184,151,66,0.15)" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#71717a" }} tickLine={false} axisLine={false} interval={4} />
              <YAxis tick={{ fontSize: 10, fill: "#71717a" }} tickLine={false} axisLine={false} tickFormatter={fmt} width={48} />
              <Tooltip
                formatter={(v) => [v.toLocaleString("en-IN"), "Views"]}
                contentStyle={{ borderRadius: 16, border: "1px solid rgba(184,151,66,0.3)", fontSize: 12 }}
              />
              <Area type="monotone" dataKey="views" stroke="#B89742" strokeWidth={2.5} fill="url(#viewsFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-8 bg-white border-2 border-gold/10 rounded-[1.5rem] p-6 md:p-8">
        <h3 className="font-display uppercase text-xl text-ink mb-5">Top Pages</h3>
        <div className="divide-y divide-gold/10">
          {data.top_pages.map((p, i) => (
            <div key={p.path} data-testid={`analytics-top-${i}`} className="py-3.5 flex items-center gap-4">
              <span className="font-display text-lg text-champagne w-8">{i + 1}</span>
              <span className="flex-1 text-sm text-ink truncate">{p.path}</span>
              <span className="text-sm font-bold text-ink/70">{fmt(p.views)}</span>
            </div>
          ))}
        </div>
      </div>

      <p className="mt-6 text-xs text-neutral-400 leading-relaxed">
        Live tracking is active now. Lifetime and historical figures include the archive baseline of {fmt(data.lifetime - data.live_views)} views
        gathered across TBOI's content history; {fmt(data.live_views)} views have been recorded live since tracking began.
      </p>
    </div>
  );
}
